/**
 * Navigation / meta cache: databases, table listings per database (sidebar tree Page → Group →
 * Table), table meta (titles, rights), favourites and recently used tables (/userdata – hidden
 * when the endpoints are missing).
 */
import { defineStore } from 'pinia';
import { computed, reactive, ref, shallowReactive } from 'vue';
import { ApiError, api, seg } from '../api/client';
import type { Listing, ListingItem, TableMeta, TableRef } from '../api/types';

export interface NavGroup {
  key: string;
  /** '' = items directly below the page */
  label: string;
  items: ListingItem[];
}
export interface NavPage {
  key: string;
  label: string;
  /** true for the ungrouped tables ("other") */
  other: boolean;
  groups: NavGroup[];
}

/** Listing → Page → Group → Table. Group paths "A | B" are pages A with group B. */
export function buildTree(listing: Listing | undefined): NavPage[] {
  const pages: NavPage[] = [];
  const byKey = new Map<string, NavPage>();
  for (const g of listing?.groups ?? []) {
    if (!g.items.length) continue;
    const other = g.group === null;
    const path = g.path.length ? g.path : [g.label];
    const pageLabel = other ? '' : path[0]!;
    const pageKey = other ? '\u0000other' : pageLabel;
    let page = byKey.get(pageKey);
    if (!page) {
      page = { key: pageKey, label: pageLabel, other, groups: [] };
      byKey.set(pageKey, page);
      pages.push(page);
    }
    const groupLabel = other ? '' : path.slice(1).join(' | ');
    let group = page.groups.find((x) => x.label === groupLabel);
    if (!group) page.groups.push((group = { key: `${pageKey}/${groupLabel}`, label: groupLabel, items: [] }));
    group.items.push(...g.items);
  }
  // "other" last
  return [...pages.filter((p) => !p.other), ...pages.filter((p) => p.other)];
}

/** Filter a tree by text (table name or translated label); empty groups/pages drop out. */
export function filterTree(pages: NavPage[], query: string, label: (s: string) => string = (s) => s): NavPage[] {
  const q = query.trim().toLowerCase();
  if (!q) return pages;
  const match = (s: string) => s.toLowerCase().includes(q);
  return pages
    .map((p) => {
      const pageHit = !p.other && match(label(p.label));
      const groups = p.groups
        .map((g) => (pageHit || (g.label && match(label(g.label))) ? g : { ...g, items: g.items.filter((i) => match(i.name) || match(label(i.label))) }))
        .filter((g) => g.items.length);
      return { ...p, groups };
    })
    .filter((p) => p.groups.length);
}

/** Normalise /userdata favourites/recent entries (tolerant to the exact server shape). */
export function normalizeRefs(body: unknown): TableRef[] {
  const list = Array.isArray(body) ? body : body && typeof body === 'object' ? ((body as Record<string, unknown>).items ?? (body as Record<string, unknown>).favorites ?? (body as Record<string, unknown>).recent ?? []) : [];
  if (!Array.isArray(list)) return [];
  const out: TableRef[] = [];
  for (const x of list) {
    if (!x || typeof x !== 'object') continue;
    const o = x as Record<string, unknown>;
    const db = (o.db ?? o.database) as unknown;
    const table = o.table as unknown;
    if (typeof db !== 'string' || typeof table !== 'string') continue;
    const r: TableRef = { db, table };
    if (typeof o.id === 'string' || typeof o.id === 'number') r.id = o.id;
    const label = o.label ?? o.title;
    if (typeof label === 'string') r.label = label;
    const at = o.at ?? o.lastAt ?? o.usedAt ?? o.used_at ?? o.updatedAt;
    if (typeof at === 'string') r.at = at;
    out.push(r);
  }
  return out;
}

const missing = (e: unknown) => e instanceof ApiError && [404, 405, 501].includes(e.status);
const RECENT_MAX = 10;

export const useNavigationStore = defineStore('navigation', () => {
  const databases = ref<Listing | null>(null);
  const tables = shallowReactive(new Map<string, Listing>());
  const tableMeta = shallowReactive(new Map<string, TableMeta>());
  const currentDb = ref<string | null>(null);
  const favorites = ref<TableRef[]>([]);
  const recent = ref<TableRef[]>([]);
  /** null = unknown, false = endpoint missing → section hidden */
  const userdata = reactive<{ favorites: boolean | null; recent: boolean | null }>({ favorites: null, recent: null });
  const inflight = new Map<string, Promise<unknown>>();

  const databaseList = computed<ListingItem[]>(() => databases.value?.groups.flatMap((g) => g.items) ?? []);

  function once<T>(key: string, fn: () => Promise<T>): Promise<T> {
    const p = inflight.get(key) as Promise<T> | undefined;
    if (p) return p;
    const run = fn().finally(() => inflight.delete(key));
    inflight.set(key, run);
    return run;
  }

  async function loadDatabases(force = false): Promise<Listing> {
    if (databases.value && !force) return databases.value;
    return once('dbs', async () => (databases.value = await api.get<Listing>('/meta/databases')));
  }

  async function loadTables(db: string, force = false): Promise<Listing> {
    const cached = tables.get(db);
    if (cached && !force) return cached;
    return once(`tables:${db}`, async () => {
      const l = await api.get<Listing>(`/meta/databases/${seg(db)}/tables`);
      tables.set(db, l);
      return l;
    });
  }

  async function loadTableMeta(db: string, table: string, force = false): Promise<TableMeta> {
    const key = `${db}.${table}`;
    const cached = tableMeta.get(key);
    if (cached && !force) return cached;
    return once(`meta:${key}`, async () => {
      const m = await api.get<TableMeta>(`/meta/tables/${seg(db)}/${seg(table)}`);
      tableMeta.set(key, m);
      return m;
    });
  }

  /** Label of a table from the cached listing (raw, may contain %LANG_x%). */
  function tableLabel(db: string, table: string): string | undefined {
    return tableMeta.get(`${db}.${table}`)?.label ?? tables.get(db)?.groups.flatMap((g) => g.items).find((i) => i.name === table)?.label;
  }

  function databaseLabel(db: string): string | undefined {
    return databaseList.value.find((d) => d.name === db)?.label;
  }

  /** Pick the current database: route param > previous > first. */
  function selectDatabase(db: string | null): void {
    currentDb.value = db;
  }

  async function loadUserdata(): Promise<void> {
    await Promise.all([
      api.get<unknown>('/userdata/favorites').then(
        (b) => ((favorites.value = normalizeRefs(b)), (userdata.favorites = true)),
        (e) => ((userdata.favorites = false), missing(e) || console.warn('favorites', e)),
      ),
      api.get<unknown>('/userdata/recent').then(
        (b) => ((recent.value = normalizeRefs(b).slice(0, RECENT_MAX)), (userdata.recent = true)),
        (e) => ((userdata.recent = false), missing(e) || console.warn('recent', e)),
      ),
    ]);
  }

  const isFavorite = (db: string, table: string) => favorites.value.some((f) => f.db === db && f.table === table);

  /** Add / remove a favourite (POST /userdata/favorites, DELETE /userdata/favorites/{db}/{table}). */
  async function toggleFavorite(db: string, table: string): Promise<void> {
    if (!userdata.favorites) return;
    const before = favorites.value;
    const same = (f: TableRef) => f.db === db && f.table === table;
    try {
      if (before.some(same)) {
        favorites.value = before.filter((f) => !same(f));
        await api.delete(`/userdata/favorites/${seg(db)}/${seg(table)}`);
      } else {
        favorites.value = [...before, { db, table }];
        // the server answers with the whole (access-filtered) list
        const list = await api.post<unknown>('/userdata/favorites', { database: db, table });
        if (list && typeof list === 'object' && Array.isArray((list as { favorites?: unknown }).favorites)) favorites.value = normalizeRefs(list);
      }
    } catch (e) {
      favorites.value = before;
      throw e;
    }
  }

  /** A table was opened: move it to the top of "recently used" (server stores it if available). */
  function touchRecent(db: string, table: string): void {
    const label = tableLabel(db, table);
    recent.value = [{ db, table, ...(label ? { label } : {}) }, ...recent.value.filter((r) => !(r.db === db && r.table === table))].slice(0, RECENT_MAX);
    if (userdata.recent) void api.post('/userdata/recent', { database: db, table }).catch((e) => missing(e) && (userdata.recent = false));
  }

  function reset(): void {
    databases.value = null;
    tables.clear();
    tableMeta.clear();
    currentDb.value = null;
    favorites.value = [];
    recent.value = [];
    userdata.favorites = null;
    userdata.recent = null;
  }

  return {
    databases,
    databaseList,
    tables,
    tableMeta,
    currentDb,
    favorites,
    recent,
    userdata,
    loadDatabases,
    loadTables,
    loadTableMeta,
    tableLabel,
    databaseLabel,
    selectDatabase,
    loadUserdata,
    isFavorite,
    toggleFavorite,
    touchRecent,
    reset,
  };
});
